<template>
  <div class="flex flex-wrap">
    <div class="w-full px-4">
      <!-- Hero Search -->
      <div class="rounded-2xl shadow-xl bg-white overflow-hidden mb-8 border border-gray-100">
        <div class="bg-respina-primary px-6 py-8 text-white">
          <h1 class="text-2xl md:text-3xl font-bold mb-1">Flight Board</h1>
          <p class="text-respina-light text-sm opacity-90">
            Search airports or flight numbers
          </p>
        </div>
        <div class="p-6 md:p-8">
          <div class="flex flex-wrap gap-3 mb-4">
            <button
              type="button"
              class="px-5 py-2.5 rounded-lg font-semibold transition-all"
              :class="searchMode === 'airport' ? 'bg-respina-primary text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
              @click="setMode('airport')"
            >
              <i class="fas fa-map-marker-alt ml-2"></i>
              Airport
            </button>
            <button
              type="button"
              class="px-5 py-2.5 rounded-lg font-semibold transition-all"
              :class="searchMode === 'flight' ? 'bg-respina-primary text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
              @click="setMode('flight')"
            >
              <i class="fas fa-plane ml-2"></i>
              Flight No.
            </button>
            <button
              type="button"
              class="px-5 py-2.5 rounded-lg font-semibold transition-all"
              :class="searchMode === 'airline' ? 'bg-respina-primary text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
              @click="setMode('airline')"
            >
              <i class="fas fa-building ml-2"></i>
              Airline
            </button>
          </div>
          <div class="flex flex-col sm:flex-row gap-3">
            <div class="flex-1 relative">
              <i class="fas fa-search absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
              <input
                type="text"
                v-model="query"
                @keydown.enter.prevent="doSearch"
                class="w-full h-14 pl-4 pr-12 text-lg border-2 border-gray-200 rounded-xl focus:border-respina-primary focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                :placeholder="
                  searchMode === 'airport'
                    ? 'e.g. New York, JFK, Los Angeles...'
                    : searchMode === 'flight'
                      ? 'e.g. AA123, UA456'
                      : 'e.g. AA, UA, American Airlines, United'
                "
              />
            </div>
            <button
              type="button"
              class="h-14 px-8 bg-respina-primary hover:bg-respina-secondary text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              @click="doSearch"
              :disabled="(searchMode === 'airport' && query.trim().length < 2) || (searchMode === 'flight' && query.trim().length < 2) || (searchMode === 'airline' && query.trim().length < 2) || loadingSearch"
            >
              {{ loadingSearch ? "Searching..." : "Search" }}
            </button>
          </div>
        </div>
      </div>

      <!-- Error -->
      <div v-if="error" class="mb-6 rounded-xl bg-red-50 border border-red-200 text-red-700 px-6 py-4 flex items-center justify-between">
        <span><i class="fas fa-exclamation-circle ml-2"></i>{{ error }}</span>
        <button type="button" class="text-red-500 hover:text-red-700" @click="error = null">×</button>
      </div>

      <!-- Airport Results -->
      <div v-if="searchMode === 'airport' && results.length && !selectedCode" class="mb-6">
        <h3 class="text-gray-700 font-bold mb-3">Airport results</h3>
        <div class="space-y-2">
          <div
            v-for="(a, idx) in results"
            :key="idx"
            class="p-4 rounded-xl border-2 border-gray-100 hover:border-respina-accent hover:bg-blue-50 cursor-pointer transition-all"
            @click="selectAirport(a)"
          >
            <div class="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div class="font-bold text-gray-800">{{ a.name }}</div>
                <div class="text-sm text-gray-500">{{ a.city || "-" }} • {{ a.country || "-" }}</div>
              </div>
              <div class="flex gap-2 items-center">
                <span v-if="a.iata" class="px-3 py-1 rounded-lg text-respina-primary bg-respina-light text-sm font-bold">IATA {{ a.iata }}</span>
                <span v-if="a.icao" class="px-3 py-1 rounded-lg text-respina-secondary bg-blue-100 text-sm font-bold">ICAO {{ a.icao }}</span>
                <button
                  type="button"
                  class="ml-2 text-gray-300 hover:text-respina-primary"
                  @click.stop="toggleFavAirport(a)"
                  title="Favorite"
                >
                  <i :class="isFavAirport(a) ? 'fas fa-star' : 'far fa-star'"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Flight Board -->
      <div
        v-if="selectedCode || ((searchMode === 'flight' || searchMode === 'airline') && (flightResults.length || loadingBoard || query.trim().length >= 2))"
        class="rounded-2xl shadow-lg bg-white overflow-hidden border border-gray-100"
      >
        <div class="bg-respina-primary px-6 py-4 text-white flex flex-wrap items-center justify-between gap-4">
          <h3 class="font-bold text-lg">
            {{
              searchMode === "airport"
                ? `Board: ${selectedCode}`
                : searchMode === "flight"
                  ? `Flight: ${query.trim().toUpperCase()}`
                  : `Airline: ${query.trim().toUpperCase()}`
            }}
            <span v-if="selectedAirportName" class="font-normal opacity-90"> — {{ selectedAirportName }}</span>
          </h3>
          <div v-if="searchMode === 'airport'" class="flex gap-2">
            <button
              type="button"
              class="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-bold transition-all"
              @click="clearSelection"
            >
              Change
            </button>
            <button
              type="button"
              class="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-bold transition-all"
              @click="refreshBoard"
              :disabled="!selectedCode || loadingBoard"
            >
              {{ loadingBoard ? "..." : "Refresh" }}
            </button>
          </div>
        </div>
        <div class="p-4">
          <div v-if="searchMode === 'airport'" class="flex flex-wrap gap-2 mb-4">
            <button
              v-for="t in ['both','arrivals','departures']"
              :key="t"
              class="px-4 py-2 rounded-lg text-sm font-bold transition-all"
              :class="tab === t ? 'bg-respina-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
              @click="setTab(t)"
            >
              {{ t === 'both' ? 'All' : t === 'arrivals' ? 'Arrivals' : 'Departures' }}
            </button>
          </div>
          <div v-if="loadingBoard" class="py-8 text-center text-gray-500">
            Loading...
          </div>
          <div v-if="board && !loadingBoard && searchMode === 'airport'" class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-gray-50 border-b border-gray-200">
                  <th class="px-4 py-3 text-left text-gray-600 font-bold">Fav</th>
                  <th class="px-4 py-3 text-left text-gray-600 font-bold">Time (UTC)</th>
                  <th class="px-4 py-3 text-left text-gray-600 font-bold">Flight</th>
                  <th class="px-4 py-3 text-left text-gray-600 font-bold">Airline</th>
                  <th class="px-4 py-3 text-left text-gray-600 font-bold">Route</th>
                  <th class="px-4 py-3 text-left text-gray-600 font-bold">Status</th>
                  <th class="px-4 py-3 text-left text-gray-600 font-bold">Terminal / Gate</th>
                  <th class="px-4 py-3 text-left text-gray-600 font-bold">Delay</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(f, idx) in flightsToShow" :key="idx" class="border-b border-gray-100 hover:bg-gray-50">
                  <td class="px-4 py-3">
                    <button
                      type="button"
                      class="text-gray-300 hover:text-respina-primary"
                      title="Favorite"
                      @click.stop="toggleFavFlight(f)"
                    >
                      <i :class="isFavFlight(f) ? 'fas fa-star' : 'far fa-star'"></i>
                    </button>
                  </td>
                  <td class="px-4 py-3 text-gray-700">{{ displayTime(f) }}</td>
                  <td class="px-4 py-3 font-bold text-respina-primary">{{ f.flight.iata || f.flight.icao || f.flight.number || "-" }}</td>
                  <td class="px-4 py-3 text-gray-700">{{ f.airline.name || f.airline.iata || f.airline.icao || "-" }}</td>
                  <td class="px-4 py-3 text-gray-700">{{ routeText(f) }}</td>
                  <td class="px-4 py-3"><span class="px-2 py-1 rounded text-xs font-bold" :class="statusBadgeClass(f.status)">{{ (f.status || "unknown").toUpperCase() }}</span></td>
                  <td class="px-4 py-3 text-gray-700">{{ terminalGateText(f) }}</td>
                  <td class="px-4 py-3 text-gray-700">{{ delayText(f) }}</td>
                </tr>
                <tr v-if="!flightsToShow.length">
                  <td class="px-4 py-8 text-gray-500 text-center" colspan="8">
                    No flights found.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else-if="(searchMode === 'flight' || searchMode === 'airline') && flightResults.length && !loadingBoard" class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-gray-50 border-b border-gray-200">
                  <th class="px-4 py-3 text-left text-gray-600 font-bold">Fav</th>
                  <th class="px-4 py-3 text-left text-gray-600 font-bold">Time (UTC)</th>
                  <th class="px-4 py-3 text-left text-gray-600 font-bold">Flight</th>
                  <th class="px-4 py-3 text-left text-gray-600 font-bold">Airline</th>
                  <th class="px-4 py-3 text-left text-gray-600 font-bold">Route</th>
                  <th class="px-4 py-3 text-left text-gray-600 font-bold">Status</th>
                  <th class="px-4 py-3 text-left text-gray-600 font-bold">Terminal / Gate</th>
                  <th class="px-4 py-3 text-left text-gray-600 font-bold">Delay</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(f, idx) in flightResults" :key="idx" class="border-b border-gray-100 hover:bg-gray-50">
                  <td class="px-4 py-3">
                    <button
                      type="button"
                      class="text-gray-300 hover:text-respina-primary"
                      title="Favorite"
                      @click.stop="toggleFavFlight(f)"
                    >
                      <i :class="isFavFlight(f) ? 'fas fa-star' : 'far fa-star'"></i>
                    </button>
                  </td>
                  <td class="px-4 py-3 text-gray-700">{{ displayTimeFlight(f) }}</td>
                  <td class="px-4 py-3 font-bold text-respina-primary">{{ f.flight.iata || f.flight.icao || f.flight.number || "-" }}</td>
                  <td class="px-4 py-3 text-gray-700">{{ f.airline.name || f.airline.iata || f.airline.icao || "-" }}</td>
                  <td class="px-4 py-3 text-gray-700">{{ routeText(f) }}</td>
                  <td class="px-4 py-3"><span class="px-2 py-1 rounded text-xs font-bold" :class="statusBadgeClass(f.status)">{{ (f.status || "unknown").toUpperCase() }}</span></td>
                  <td class="px-4 py-3 text-gray-700">{{ terminalGateTextFlight(f) }}</td>
                  <td class="px-4 py-3 text-gray-700">{{ delayTextFlight(f) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else-if="(searchMode === 'flight' || searchMode === 'airline') && !loadingBoard && query.trim().length >= 2 && !flightResults.length" class="py-8 text-center text-gray-500">
            No results found.
          </div>
        </div>
      </div>
    </div>

    <!-- Login Modal (shown when unauthenticated user tries to favorite) -->
    <div
      v-if="showLoginModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8"
    >
      <div
        class="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-orange-100 overflow-hidden"
      >
        <div class="bg-respina-primary/90 px-6 py-4 text-white flex items-center justify-between">
          <h2 class="text-base md:text-lg font-bold">Sign in to save favorites</h2>
          <button type="button" class="text-gray-400 hover:text-gray-600" @click="closeLoginModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="p-6">
          <p class="text-sm text-gray-600 mb-6">
            Create an account or sign in to keep your favorite airports and flights in sync.
          </p>
          <form class="space-y-5" @submit.prevent="handleLoginSubmit">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                v-model="loginEmail"
                type="email"
                required
                class="w-full h-11 px-3 rounded-lg border border-gray-300 focus:border-respina-primary focus:ring-2 focus:ring-blue-200 outline-none text-sm"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                v-model="loginPassword"
                type="password"
                required
                class="w-full h-11 px-3 rounded-lg border border-gray-300 focus:border-respina-primary focus:ring-2 focus:ring-blue-200 outline-none text-sm"
              />
            </div>
            <div v-if="loginError" class="text-sm text-red-600">
              {{ loginError }}
            </div>
            <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-4">
              <button
                type="button"
                class="text-xs text-respina-primary hover:text-respina-secondary"
                @click="goToRegister"
              >
                Don't have an account? Register
              </button>
              <div class="flex items-center justify-end gap-3">
                <button
                  type="button"
                  class="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800"
                  @click="closeLoginModal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  class="px-5 py-2 text-sm font-bold rounded-lg bg-respina-primary text-white hover:bg-respina-secondary disabled:opacity-60"
                  :disabled="loginLoading"
                >
                  {{ loginLoading ? "Signing in..." : "Sign in" }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiGet } from "@/lib/http";
import { getFavorites, loadFavorites, onFavoritesChanged, toggleFavorite } from "@/lib/favorites";
import { isAuthenticated, login } from "@/lib/auth";

export default {
  data() {
    return {
      favorites: [],
      searchMode: "airport",
      query: "",
      results: [],
      datasetTotal: null,
      loadingSearch: false,
      selectedCode: null,
      selectedAirportName: null,
      tab: "both",
      board: null,
      flightResults: [],
      loadingBoard: false,
      error: null,
      routeSync: false,
      // Login modal state
      showLoginModal: false,
      loginEmail: "",
      loginPassword: "",
      loginLoading: false,
      loginError: null,
      pendingFav: null,
    };
  },
  mounted() {
    this.applyRouteQuery();
    loadFavorites().then(() => {
      this.favorites = getFavorites();
    }).catch(() => {});
    this.offFav = onFavoritesChanged(() => {
      this.favorites = getFavorites();
    });
  },
  beforeUnmount() {
    this.offFav?.();
  },
  watch: {
    "$route.query": {
      handler() {
        this.applyRouteQuery();
      },
      deep: true,
    },
  },
  computed: {
    flightsToShow() {
      if (!this.board) return [];
      if (this.tab === "arrivals") return this.board.arrivals?.flights || [];
      if (this.tab === "departures") return this.board.departures?.flights || [];
      const a = (this.board.arrivals?.flights || []).map((f) => ({ ...f, _kind: "A" }));
      const d = (this.board.departures?.flights || []).map((f) => ({ ...f, _kind: "D" }));
      const all = [...a, ...d];
      all.sort((x, y) => {
        const tx = this.displayTime(x) || "";
        const ty = this.displayTime(y) || "";
        return tx.localeCompare(ty);
      });
      return all;
    },
  },
  methods: {
    async safeReplaceQuery(query) {
      const cur = this.$route?.query || {};
      const a = JSON.stringify(cur || {});
      const b = JSON.stringify(query || {});
      if (a === b) return;
      this.routeSync = true;
      try {
        await this.$router.replace({ query });
      } finally {
        // Let the router settle, then allow watcher to run again.
        setTimeout(() => {
          this.routeSync = false;
        }, 0);
      }
    },
    setMode(m) {
      this.searchMode = m;
      this.error = null;
      this.results = [];
      this.flightResults = [];
      this.board = null;
      this.selectedCode = null;
      this.selectedAirportName = null;
      this.query = "";
      this.safeReplaceQuery({});
    },
    doSearch() {
      if (this.searchMode === "airport") this.search();
      else if (this.searchMode === "flight") this.searchByFlight();
      else this.searchByAirline();
    },
    async search() {
      // If an airport is currently selected, user is starting a new search.
      // Clear selection so results list can show again.
      if (this.selectedCode) {
        this.selectedCode = null;
        this.selectedAirportName = null;
        this.board = null;
        this.safeReplaceQuery({});
      }
      this.loadingSearch = true;
      this.error = null;
      this.results = [];
      this.datasetTotal = null;
      try {
        const q = this.query.trim();
        const data = await apiGet(`/api/airports/search?q=${encodeURIComponent(q)}&limit=10`);
        this.results = data?.results || [];
        this.datasetTotal = data?.datasetTotal || null;
      } catch (e) {
        this.error = e?.message || "Search failed";
      } finally {
        this.loadingSearch = false;
      }
    },
    async searchByFlight() {
      this.loadingSearch = true;
      this.loadingBoard = true;
      this.error = null;
      this.flightResults = [];
      try {
        const q = this.query.trim();
        const data = await apiGet(`/api/flights/by-number?flight=${encodeURIComponent(q)}&limit=50`);
        this.flightResults = data?.flights || [];
        await this.safeReplaceQuery({ mode: "flight", flight: q });
      } catch (e) {
        this.error = e?.message || "Flight search failed";
      } finally {
        this.loadingSearch = false;
        this.loadingBoard = false;
      }
    },
    async searchByAirline() {
      this.loadingSearch = true;
      this.loadingBoard = true;
      this.error = null;
      this.flightResults = [];
      try {
        const q = this.query.trim();
        const data = await apiGet(`/api/flights/by-airline?airline=${encodeURIComponent(q)}&limit=50`);
        this.flightResults = data?.flights || [];
        await this.safeReplaceQuery({ mode: "airline", airline: q });
      } catch (e) {
        this.error = e?.message || "Airline search failed";
      } finally {
        this.loadingSearch = false;
        this.loadingBoard = false;
      }
    },
    async selectAirport(a) {
      this.error = null;
      this.selectedCode = (a.iata || a.icao || "").toUpperCase();
      this.selectedAirportName = a.name || null;
      this.results = [];
      await this.refreshBoard();
    },
    setTab(t) {
      this.tab = t;
    },
    async refreshBoard() {
      if (!this.selectedCode) return;
      this.loadingBoard = true;
      this.error = null;
      try {
        const data = await apiGet(
          `/api/flights/board?airport=${encodeURIComponent(this.selectedCode)}&type=both&limit=50`
        );
        this.board = data;
        await this.safeReplaceQuery({ mode: "airport", airport: this.selectedCode });
      } catch (e) {
        this.error = e?.message || "Board fetch failed";
      } finally {
        this.loadingBoard = false;
      }
    },
    clearSelection() {
      this.selectedCode = null;
      this.selectedAirportName = null;
      this.board = null;
      this.safeReplaceQuery({});
    },
    airportCode(a) {
      return String((a?.iata || a?.icao || "")).toUpperCase();
    },
    isFavAirport(a) {
      const code = this.airportCode(a);
      if (!code) return false;
      const v = code.toUpperCase();
      return this.favorites.some((x) => x?.type === "airport" && x?.value === v);
    },
    async toggleFavAirport(a) {
      const code = this.airportCode(a);
      if (!code) return;
      if (!isAuthenticated()) {
        this.pendingFav = { type: "airport", value: code, label: `Airport ${code}` };
        this.openLoginModal();
        return;
      }
      try {
        await toggleFavorite({ type: "airport", value: code, label: `Airport ${code}` });
      } catch (e) {
        this.error = e?.message || "Favorite failed";
      }
    },
    flightId(f) {
      return String(f?.flight?.iata || f?.flight?.icao || f?.flight?.number || "").toUpperCase();
    },
    isFavFlight(f) {
      const id = this.flightId(f);
      if (!id) return false;
      const v = id.toUpperCase();
      return this.favorites.some((x) => x?.type === "flight" && x?.value === v);
    },
    async toggleFavFlight(f) {
      const id = this.flightId(f);
      if (!id) return;
      if (!isAuthenticated()) {
        this.pendingFav = { type: "flight", value: id, label: `Flight ${id}` };
        this.openLoginModal();
        return;
      }
      try {
        await toggleFavorite({ type: "flight", value: id, label: `Flight ${id}` });
      } catch (e) {
        this.error = e?.message || "Favorite failed";
      }
    },
    async applyRouteQuery() {
      if (this.routeSync) return;
      const q = this.$route?.query || {};
      const mode = String(q.mode || "").toLowerCase();
      if (mode === "airport" && q.airport) {
        const code = String(q.airport).toUpperCase();
        this.searchMode = "airport";
        this.selectedCode = code;
        this.selectedAirportName = null;
        this.results = [];
        this.query = code;
        await this.refreshBoard();
        return;
      }
      if (mode === "flight" && q.flight) {
        const flight = String(q.flight).toUpperCase();
        this.searchMode = "flight";
        this.selectedCode = null;
        this.board = null;
        this.query = flight;
        await this.searchByFlight();
        return;
      }
      if (mode === "airline" && q.airline) {
        const airline = String(q.airline).toUpperCase();
        this.searchMode = "airline";
        this.selectedCode = null;
        this.board = null;
        this.query = airline;
        await this.searchByAirline();
        return;
      }
    },
    statusBadgeClass(status) {
      const s = String(status || "").toLowerCase();
      if (s === "active") return "text-respina-primary bg-respina-light";
      if (s === "landed") return "text-gray-700 bg-gray-200";
      if (s === "scheduled") return "text-respina-secondary bg-blue-100";
      if (s === "cancelled") return "text-red-600 bg-red-200";
      if (s === "diverted" || s === "incident") return "text-orange-600 bg-orange-200";
      return "text-gray-700 bg-gray-100";
    },
    displayTime(f) {
      if (this.tab === "arrivals") return f?.arrival?.scheduled || f?.arrival?.estimated || "";
      if (this.tab === "departures") return f?.departure?.scheduled || f?.departure?.estimated || "";
      if (f?._kind === "A") return f?.arrival?.scheduled || f?.arrival?.estimated || "";
      if (f?._kind === "D") return f?.departure?.scheduled || f?.departure?.estimated || "";
      return f?.departure?.scheduled || f?.arrival?.scheduled || "";
    },
    displayTimeFlight(f) {
      return f?.departure?.scheduled || f?.departure?.estimated || f?.arrival?.scheduled || f?.arrival?.estimated || "";
    },
    routeText(f) {
      const dep = f?.departure?.iata || f?.departure?.icao || "-";
      const arr = f?.arrival?.iata || f?.arrival?.icao || "-";
      return `${dep} → ${arr}`;
    },
    terminalGateText(f) {
      const term = (this.tab === "arrivals" ? f?.arrival?.terminal : f?.departure?.terminal) || "-";
      const gate = (this.tab === "arrivals" ? f?.arrival?.gate : f?.departure?.gate) || "-";
      return `${term} / ${gate}`;
    },
    terminalGateTextFlight(f) {
      const term = f?.departure?.terminal || f?.arrival?.terminal || "-";
      const gate = f?.departure?.gate || f?.arrival?.gate || "-";
      return `${term} / ${gate}`;
    },
    delayText(f) {
      const depDelay = f?.departure?.delay;
      const arrDelay = f?.arrival?.delay;
      if (this.tab === "arrivals") return arrDelay != null ? `${arrDelay}m` : "-";
      if (this.tab === "departures") return depDelay != null ? `${depDelay}m` : "-";
      const d = depDelay != null ? `dep ${depDelay}m` : null;
      const a = arrDelay != null ? `arr ${arrDelay}m` : null;
      return [d, a].filter(Boolean).join(", ") || "-";
    },
    delayTextFlight(f) {
      const depDelay = f?.departure?.delay;
      const arrDelay = f?.arrival?.delay;
      if (depDelay != null) return `dep ${depDelay}m`;
      if (arrDelay != null) return `arr ${arrDelay}m`;
      return "-";
    },
    openLoginModal() {
      this.loginError = null;
      this.loginLoading = false;
      this.showLoginModal = true;
    },
    closeLoginModal() {
      this.showLoginModal = false;
      this.pendingFav = null;
    },
    goToRegister() {
      this.showLoginModal = false;
      this.$router.push("/auth/register");
    },
    async handleLoginSubmit() {
      this.loginError = null;
      this.loginLoading = true;
      try {
        await login(this.loginEmail, this.loginPassword);
        // Reload favorites after a successful login so sidebar and board stay in sync.
        try {
          await loadFavorites();
          this.favorites = getFavorites();
        } catch {
          // ignore favorites reload errors
        }
        this.showLoginModal = false;
        await this.retryPendingFavorite();
        this.pendingFav = null;
      } catch (e) {
        this.loginError = e?.message || "Login failed";
      } finally {
        this.loginLoading = false;
      }
    },
    async retryPendingFavorite() {
      if (!this.pendingFav) return;
      const p = this.pendingFav;

      // If this item is already in the favorites list after login, do nothing
      // (just let the UI reflect the existing favorite state).
      const alreadyFav = this.favorites.some(
        (x) => x?.type === p.type && x?.value === p.value,
      );
      if (alreadyFav) return;

      try {
        if (p.type === "airport") {
          await toggleFavorite({ type: "airport", value: p.value, label: p.label });
        } else if (p.type === "flight") {
          await toggleFavorite({ type: "flight", value: p.value, label: p.label });
        }
      } catch (e) {
        this.error = e?.message || "Favorite failed";
      }
    },
  },
};
</script>
